import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { MessageSquare, Star, ThumbsUp } from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import EmptyState from "@/components/shared/EmptyState";
import { SearchInput } from "@/components/shared/SearchInput";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuthStore } from "@/store/authStore";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/admin/feedback/")({
  component: AdminFeedbackPage,
});

const mockFeedback = [
  {
    id: "1",
    user: "John Mwangi",
    type: "student",
    rating: 5,
    comment: "Excellent platform, very helpful for finding placements within my field of study.",
    date: "16/04/2026",
    helpful: 12,
  },
  {
    id: "2",
    user: "CRDB Bank",
    type: "partner",
    rating: 4,
    comment:
      "Good quality students, the work log management feature is quite intuitive for our supervisors.",
    date: "15/04/2026",
    helpful: 8,
  },
  {
    id: "3",
    user: "Sarah Kimaro",
    type: "student",
    rating: 3,
    comment:
      "Platform is okay, could use better search filters when looking for specific partner industries.",
    date: "14/04/2026",
    helpful: 5,
  },
];

function AdminFeedbackPage() {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const { user } = useAuthStore();

  const displayFeedback = user?.isDemo ? mockFeedback : [];

  const filteredFeedback = displayFeedback.filter((f) => {
    const matchesSearch =
      f.comment.toLowerCase().includes(search.toLowerCase()) ||
      f.user.toLowerCase().includes(search.toLowerCase());
    const matchesType = typeFilter === "all" || f.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const avgRating =
    displayFeedback.length > 0
      ? displayFeedback.reduce((sum, f) => sum + f.rating, 0) / displayFeedback.length
      : 0;

  return (
    <div className="space-y-6 pb-4">
      <PageHeader
        title="Experience sentiment"
        subtitle="Monitor platform user satisfaction, aggregate ratings, and qualitative experience reports."
        breadcrumbs={[{ label: "Dashboard", path: "/admin/dashboard" }, { label: "Feedback" }]}
      />

      {/* Sentiment Overview */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 md:gap-4">
        <StatCard label="Total submissions" value={displayFeedback.length} icon={<MessageSquare />} />
        <StatCard
          label="Platform average"
          value={avgRating > 0 ? avgRating.toFixed(1) : "-"}
          icon={<Star />}
        />
        <StatCard
          label="Net positive ratio"
          value={
            displayFeedback.length > 0
              ? `${Math.round((displayFeedback.filter((f) => f.rating >= 4).length / displayFeedback.length) * 100)}%`
              : "-"
          }
          icon={<ThumbsUp />}
        />
      </div>

      {/* Search and Filters */}
      <SectionCard
        title="Recent feedback"
        actions={
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search sentiment…"
              className="min-w-0 flex-1 sm:w-60 sm:flex-none"
            />
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="h-8 w-[150px]" aria-label="Audience">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All audiences</SelectItem>
                <SelectItem value="student">Students</SelectItem>
                <SelectItem value="partner">Partners</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
        noPadding
      >
        {filteredFeedback.length === 0 ? (
          <EmptyState
            bare
            icon={<MessageSquare />}
            title="No feedback yet"
            description="Ratings and comments from students and partners will appear here."
          />
        ) : (
          <ul className="divide-y divide-border">
            {filteredFeedback.map((item) => (
              <li key={item.id} className="px-4 py-4 transition-colors hover:bg-muted/40 md:px-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <Avatar className="size-9 rounded-full">
                      <AvatarFallback className="rounded-full text-sm">
                        {item.user.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="truncate text-sm font-medium text-foreground">{item.user}</h3>
                        <Badge variant={item.type === "student" ? "outline" : "info"} className="capitalize">
                          {item.type}
                        </Badge>
                      </div>
                      <RatingStars value={item.rating} />
                    </div>
                  </div>
                  <span className="shrink-0 font-mono text-caption text-muted-foreground tabular-nums">
                    {item.date}
                  </span>
                </div>
                <p className="mt-3 max-w-3xl text-sm leading-6 text-body">"{item.comment}"</p>
                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1">
                  <span className="inline-flex items-center gap-1.5 text-caption text-muted-foreground">
                    <ThumbsUp className="size-3.5" aria-hidden />
                    {item.helpful} users found this insightful
                  </span>
                  <Button variant="link" size="sm" className="h-auto text-caption">
                    Public response
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}

function RatingStars({ value }: { value: number }) {
  return (
    <div className="mt-1 flex items-center gap-0.5" role="img" aria-label={`Rated ${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          aria-hidden
          className={cn(
            "size-3.5",
            n <= value ? "fill-current text-foreground" : "text-muted-foreground/50",
          )}
        />
      ))}
    </div>
  );
}
